import React from 'react';
import { openPdf } from '../../../../../../entities/procedures/pdf/shared';
import { format, isAfter, isBefore, parse, isEqual } from 'date-fns';
import { createPdf } from '../../../../../../../vendor/pdfMake';
import { Room } from '../../../../../../../types/Room';
import StaffShift from '../../../../../../../types/StaffShift';
import { Procedure } from '../../../../../../../types/Procedure';
import { ZonedDateTime } from '@js-joda/core';

export interface StaffShiftExtended extends StaffShift {
  isProcedure?: boolean;
}

export type RoomWithProcedureFlag = Omit<Room, 'staffShifts'> & { staffShifts: Array<StaffShiftExtended> };

function toPairs(array: Room[]): any {
  const result: Room[][] = [];
  for (let i = 0; i < array.length; i += 2) {
    result.push([array?.[i], array?.[i + 1]]);
  }
  return result;
}

function isAfterTime(time1?: string, time2?: string) {
  if (!time1 || !time2) return undefined;
  // Define a base date to parse the time strings (e.g., January 1, 1970)
  const baseDate = '1970-01-01';

  // Parse the time strings into Date objects
  const date1 = parse(`${baseDate}T${time1.length < 5 ? `0${time1}`: time1}`);
  const date2 = parse(`${baseDate}T${time2}`);

  return isAfter(date1, date2);
}

function isBeforeTime(time1?: string, time2?: string) {
  if (!time1 || !time2) return undefined;
  // Define a base date to parse the time strings (e.g., January 1, 1970)
  const baseDate = '1970-01-01';

  // Parse the time strings into Date objects
  const date1 = parse(`${baseDate}T${time1.length < 5 ? `0${time1}`: time1}`);
  const date2 = parse(`${baseDate}T${time2}`);

  return isBefore(date1, date2);
}

const getTimeRow = (startTime: string, endTime: string, procedures1: Procedure[], procedures2: Procedure[], fillColor: string = '#eeffee', bold: boolean = false) => {
  const p1 = procedures1?.find(e => isAfterTime(e.startTimeText, startTime) && isBeforeTime(e.startTimeText, endTime));
  const p2 = procedures2?.find(e => isAfterTime(e.startTimeText, startTime) && isBeforeTime(e.startTimeText, endTime));
  return [
    { text: startTime, bold: bold, style: 'tableCell', border: [true, false, true, false], },
    p1 ? { fillColor: fillColor, rowSpan: Math.ceil((p1?.duration || 0) / 15) || 1, text: `${p1?.physician?.name || ''}  ${p1?.patientName || ''} ${p1?.patientSex || ''}${p1?.patientAge || ''}   ${p1?.anesthesiologist?.name || ''} ${p1?.anesthesiaType || ''}`, style: 'tableCell', border: [true, true, true, true] } : { text: '', style: 'tableCell', border: [false, false, true, false] },
    p2 ? { fillColor: fillColor, rowSpan: Math.ceil((p2?.duration || 0) / 15) || 1, text: `${p2?.physician?.name || ''}  ${p2?.patientName || ''} ${p2?.patientSex || ''}${p2?.patientAge || ''}   ${p2?.anesthesiologist?.name || ''} ${p2?.anesthesiaType || ''}`, style: 'tableCell', border: [true, true, true, true] } : { text: '', style: 'tableCell', border: [false, false, true, false] },
  ];
}

const getContent = (hospitalName: string, date: Date, rooms: RoomWithProcedureFlag[], operationRooms: Room[]) => {

  const data = toPairs(operationRooms);

  const tables = data.map(e => {

    return [
      [{ text: '', style: 'tableHeader' }, { text: e?.[0]?.name || '', style: 'tableHeader' }, {
        text: e?.[1]?.name || '',
        style: 'tableHeader',
      }],

      ...Array.from({ length: 18 - 6 + 1 }, (v, i) => i + 6).flatMap(i => {

        const hour = i > 9 ? `${i}` : `0${i}`;
        const hourNext = i + 1 > 9 ? `${i+1}` : `0${i+1}`;
        return [
          getTimeRow(`${hour}:00`, `${hour}:15`, e?.[0]?.procedures, e?.[1]?.procedures, '#eeffee', true),
          getTimeRow(`${hour}:15`, `${hour}:30`, e?.[0]?.procedures, e?.[1]?.procedures, '#eeeeff'),
          getTimeRow(`${hour}:30`, `${hour}:45`, e?.[0]?.procedures, e?.[1]?.procedures, '#eeffee'),
          getTimeRow(`${hour}:45`, `${hourNext}:00`, e?.[0]?.procedures, e?.[1]?.procedures, '#eeeeff'),
        ]
      }),
    ]
  });

  return {
    stack: tables.map((table) =>
      ({
        pageBreak: 'after',
        style: 'tableExample',
        table: {
          headerRows: 1,
          widths: ['5%', '*', '*'],
          body: table,
        },
      })),
  };
};

export const ORSchedulePdfGenerator = (
  hospitalName: string,
  date: Date,
  rooms: RoomWithProcedureFlag[],
  operationRooms: Room[],
) => {
  const docDefinition = {
    pageSize: 'LETTER',
    pageMargins: [40, 40, 40, 40],
    defaultStyle: {
      fontSize: 9,
      lineHeight: 1.4,
    },
    info: { title: `OR Schedule` },
    content: [
      getContent(
        hospitalName,
        date,
        rooms.map(room => ({ ...room, staffShifts: room.staffShifts.filter(staff => !!staff.staff?.name) })),
        operationRooms,
      ),
    ],
    styles: {
      tableHeader: {
        bold: true,
        fontSize: 13,
        color: 'black',
        alignment: 'center',
      },
      tableExample: {},
      tableCell: {},
    },
  };

  openPdf(createPdf(docDefinition), `OR_Schedule${format(date, 'MM_DD_YYYY_HH_mm_ss')}`);
};
